import React from 'react';
import { motion } from 'framer-motion';
import { Star } from 'lucide-react';
interface Testimonial {
  initials: string;
  name: string;
  location: string;
  text: string;
  color: string;
  pack?: string;
}
interface TestimonialsProps {
  testimonials: Testimonial[];
  title?: string;
  className?: string;
}
export function Testimonials({
  testimonials,
  title,
  className = ''
}: TestimonialsProps) {
  return <section className={`py-16 bg-zinc-900 ${className}`} data-id="element-308">
      <div className="container mx-auto px-4" data-id="element-309">
        {title && <h2 className="font-bold text-center mb-12 text-white text-[16px] md:text-2xl" data-id="element-310">
            {title}
          </h2>}

        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 max-w-6xl mx-auto" data-id="element-311">
          {testimonials.map((t, i) => <motion.div key={i} initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.5,
          delay: i * 0.1
        }} className="bg-black/70 p-6 rounded-2xl shadow-lg border border-violet-900/40 flex flex-col h-full" data-id="element-312">
              <div className="flex items-center gap-1 mb-4" data-id="element-313">
                {[...Array(5)].map((_, j) => <Star key={j} className="w-4 h-4 text-violet-400 fill-violet-400" data-id="element-314" />)}
              </div>

              <p className="text-zinc-100 italic mb-6 flex-grow" data-id="element-315">"{t.text}"</p>

              <div className="flex items-center gap-3 mt-auto pt-4 border-t border-violet-900/30" data-id="element-316">
                <div className={`w-10 h-10 rounded-full flex items-center justify-center text-white font-bold text-sm ${t.color}`} data-id="element-317">
                  {t.initials}
                </div>
                <div data-id="element-318">
                  <p className="font-bold text-white text-sm" data-id="element-319">{t.name}</p>
                  <p className="text-xs text-violet-200" data-id="element-320">{t.location}</p>
                  {t.pack && <p className="text-xs font-medium text-violet-300 mt-0.5" data-id="element-321">
                      {t.pack}
                    </p>}
                </div>
              </div>
            </motion.div>)}
        </div>
      </div>
    </section>;
}
export const initialTestimonials = [{
  initials: 'SC',
  name: 'Sofía C.',
  location: 'Emprendedora de indumentaria · Córdoba',
  text: 'Llevaba meses publicando sin resultados. Abrí el Tomo 1 y el Tomo 2 el primer día y por primera vez entendí por qué nadie me compraba. No era que mi producto era malo. Era que no tenía sistema para comunicarlo.',
  color: 'bg-violet-600'
}, {
  initials: 'ML',
  name: 'Mariela L.',
  location: 'Emprendedora de cosmética artesanal · Rosario',
  text: 'El banco de frases y los copies de WhatsApp me cambiaron todo. Antes improvisaba cada mensaje y se me iban las ventas. Ahora tengo respuestas listas para cada situación y cerré más ventas en una semana que en el mes anterior.',
  color: 'bg-purple-600'
}, {
  initials: 'PV',
  name: 'Paula V.',
  location: 'Emprendedora de accesorios · Mendoza',
  text: 'Tengo un negocio de accesorios desde hace dos años pero nunca supe cómo usar bien Instagram. El Tomo 3 y el Tomo 5 me dieron exactamente lo que necesitaba: secuencias de stories y diseños listos. Ahora publico todos los días sin bloquearme.',
  color: 'bg-fuchsia-600'
}];
export const moreTestimonials = [{
  initials: 'JM',
  name: 'Juliana M.',
  location: '🏙️ Buenos Aires',
  pack: 'Mega Pack Emprendedora',
  text: 'Antes del año pasado publicaba sin sistema y terminé el año sin saber qué había funcionado. Este sistema me hizo entender que el problema no era mi producto — era que no tenía contenido planificado, no hacía seguimiento y no sabía cómo cerrar una venta por WhatsApp. Ahora tengo un método.',
  color: 'bg-violet-700'
}, {
  initials: 'DS',
  name: 'Daniela S.',
  location: '🏙️ Santa Fe',
  pack: 'Mega Pack Emprendedora',
  text: 'El Tomo 3 me cambió completamente cómo uso las stories. Antes las subía solo cuando tenía algo que mostrar. Ahora tengo secuencias completas para cada situación. La diferencia entre subir una story y subir una secuencia de venta es enorme.',
  color: 'bg-purple-700'
}, {
  initials: 'CP',
  name: 'Carla P.',
  location: '🏙️ Tucumán',
  pack: 'Mega Pack + Sistema Pro',
  text: 'El Sistema Pro me dio el dashboard de ventas que siempre quise tener. Ahora sé exactamente cuántas consultas llegaron, cuántas se convirtieron en ventas y dónde se pierden. No es magia — es información real para tomar mejores decisiones.',
  color: 'bg-fuchsia-700'
}, {
  initials: 'AR',
  name: 'Alejandra R.',
  location: '🏙️ La Plata',
  pack: 'Mega Pack Emprendedora',
  text: 'El Tomo 6 sobre mentalidad y organización debería ser obligatorio para cualquiera que emprende sola. Entendí qué era el bloqueo creativo cuando ya había pasado semanas sin publicar. Con el sistema de rutinas del tomo eso no me pasó más.',
  color: 'bg-violet-800'
}, {
  initials: 'MG',
  name: 'Marcela G.',
  location: '🏙️ Rosario',
  pack: 'Mega Pack + Sistema Pro',
  text: 'Soy diseñadora gráfica y pensé que el Tomo 5 no me iba a aportar nada. Me equivoqué — el sistema visual de contenido para Instagram tiene su propia lógica que va más allá del diseño. El Sistema Pro me enseñó a medir qué diseños convierten y cuáles solo suman likes.',
  color: 'bg-purple-800'
}];